import React, { useState } from 'react';
import { authService } from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';
import { 
  Button, 
  TextField, 
  Container, 
  Typography, 
  Box, 
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  Avatar,
  ImageList,
  ImageListItem,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { User } from '../../types';
import { storage } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { differenceInYears, format } from 'date-fns';

const countries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
  "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Fiji", "Finland", "France",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hong Kong", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast",
  "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
  "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
  "Oman",
  "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar",
  "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Yemen",
  "Zambia", "Zimbabwe"
];

const RegisterForm: React.FC<{ onToggleForm: () => void }> = ({ onToggleForm }) => {
  const { updateUserProfile } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<Partial<User>>({
    displayName: '',
    birthdate: '',
    age: '',
    gender: '',
    location: '',
    bio: '',
    interests: '',
    occupation: '',
    education: '',
    lookingFor: '',
    height: '',
    genderPreference: '',
    ethnicities: [],
    nationality: [],
  });

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [photoError, setPhotoError] = useState(false);
  const [photoErrorMessage, setPhotoErrorMessage] = useState<string>('');
  const [verificationSent, setVerificationSent] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultipleSelect = (event: SelectChangeEvent<string[]>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const files = Array.from(e.target.files);
      const totalPhotos = imageFiles.length + files.length;
      
      if (totalPhotos > 6) {
        setPhotoError(true);
        setPhotoErrorMessage('Maximum 6 photos allowed');
        return;
      }
      
      setImageFiles(prev => [...prev, ...files]);
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setImagePreviews(prev => [...prev, ...newPreviews]);
      setPhotoError(false);
      setPhotoErrorMessage('');
      setError('');
    }
  };

  const handleRemoveImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadImages = async (uid: string): Promise<string[]> => {
    if (imageFiles.length === 0) return [];
    
    const uploadPromises = imageFiles.map(async (file, index) => {
      const profileStorageRef = ref(storage, `profile-pictures/${uid}/${Date.now()}-${index}`);
      await uploadBytes(profileStorageRef, file);
      return getDownloadURL(profileStorageRef);
    });
    
    return Promise.all(uploadPromises);
  };

  const calculateAge = (birthdate: Date): string => {
    const age = differenceInYears(new Date(), birthdate);
    return age.toString();
  };

  const handleBirthdateChange = (date: Date | null) => {
    if (!date || isNaN(date.getTime())) return;

    const minAge = 18;
    const maxAge = 100;
    const age = differenceInYears(new Date(), date);

    if (age < minAge) {
      setError(`You must be at least ${minAge} years old to register`);
      return;
    }

    if (age > maxAge) {
      setError(`Please enter a valid birthdate`);
      return;
    }

    setFormData(prev => ({
      ...prev,
      birthdate: date.toISOString(),
      age: age.toString()
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (imageFiles.length === 0) {
      setPhotoError(true);
      setPhotoErrorMessage('At least one photo is required');
      const photoSection = document.querySelector('#photo-upload-section');
      photoSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!formData.birthdate) {
      setError('Please enter your birthdate');
      return;
    }
    
    try {
      const user = await authService.register(email, password);
      if (user) {
        const photoUrls = await uploadImages(user.uid);
        
        await updateUserProfile(user.uid, {
          ...formData,
          email: email,
          verificationStatus: 'none',
          verified: false,
          matches: [],
          profilePicture: photoUrls[0] || '',
          additionalPhotos: photoUrls.slice(1),
          emailVerified: false,
        });
        
        setVerificationSent(true);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (verificationSent) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="success">
            <AlertTitle>Registration Successful!</AlertTitle>
            We've sent a verification email to {email}. Please check your inbox and click the verification link to complete your registration.
          </Alert>
          <Typography variant="body1">
            Once you verify your email, you can proceed to login.
          </Typography>
          <Button 
            onClick={() => navigate('/login')}
            variant="contained"
            fullWidth
            sx={{ mt: 2, color: 'white' }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <img 
          src="/mylogo.png" 
          alt="Love is Green Logo" 
          style={{
            height: '150px',
            objectFit: 'contain',
            width: 'auto',
            marginBottom: '10px'
          }}
        />
        </Box>
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h4" component="h2" align="center">
          Register
        </Typography>
        
        {error && <Alert severity="error">{error}</Alert>}
        {photoError && photoErrorMessage && <Alert severity="error">{photoErrorMessage}</Alert>}
        
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            
            <Box 
              id="photo-upload-section"
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                mb: 2,
                width: '100%'
              }}
            >
              <ImageList sx={{ width: '100%', height: 'auto' }} cols={3} rowHeight={164}>
                {imagePreviews.map((preview, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={preview}
                      alt={`Profile photo ${index + 1}`}
                      loading="lazy"
                      style={{ height: '164px', objectFit: 'cover' }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                      }}
                      onClick={() => handleRemoveImage(index)}
                    >
                      <DeleteIcon sx={{ color: 'white' }} />
                    </IconButton>
                    {index === 0 && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: 8,
                          color: 'white',
                          bgcolor: 'rgba(0, 0, 0, 0.5)',
                          px: 1,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                        }}
                      >
                        Main Photo
                      </Typography>
                    )}
                  </ImageListItem>
                ))}
              </ImageList>

              {imagePreviews.length < 6 && (
                <FormControl error={photoError} sx={{ width: '100%', alignItems: 'center' }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AddAPhotoIcon />}
                    color={photoError ? "error" : "primary"}
                  >
                    {imagePreviews.length === 0 ? 'Upload Photos *' : 'Add More Photos'}
                    <input
                      name="profilePhoto"
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                      multiple
                    />
                  </Button>
                  {photoError && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {photoErrorMessage}
                    </Typography>
                  )}
                </FormControl>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Upload up to 6 photos. First photo will be your main profile picture.
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              label="Display Name"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              required
            />

            <DatePicker
              label="Birthdate *"
              value={formData.birthdate ? new Date(formData.birthdate) : null}
              onChange={handleBirthdateChange}
              disableFuture
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!error && error.includes('birthdate'),
                  helperText: error && error.includes('birthdate') ? error : '',
                },
              }}
            />

            {formData.age && (
              <Typography variant="body2" color="text.secondary">
                Age: {formData.age} years old
              </Typography>
            )}

            <FormControl fullWidth required>
              <InputLabel>Gender</InputLabel>
              <Select
                name="gender"
                value={formData.gender || ''}
                label="Gender"
                onChange={handleChange}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Nonbinary</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Interested In</InputLabel>
              <Select
                name="genderPreference"
                value={formData.genderPreference || ''}
                label="Interested In"
                onChange={handleChange}
              >
                <MenuItem value="men">Men</MenuItem>
                <MenuItem value="women">Women</MenuItem>
                <MenuItem value="nonbinary">Nonbinary People</MenuItem>
                <MenuItem value="everyone">Everyone</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Bio"
              name="bio"
              multiline
              rows={3}
              value={formData.bio}
              onChange={handleChange}
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Looking For</InputLabel>
              <Select
                name="lookingFor"
                value={formData.lookingFor || ''}
                label="Looking For"
                onChange={handleChange}
              >
                <MenuItem value="relationship">Relationship</MenuItem>
                <MenuItem value="friendship">Friendship</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Height</InputLabel>
              <Select
                name="height"
                value={formData.height || ''}
                label="Height"
                onChange={handleChange}
              >
                {Array.from({ length: 40 }, (_, i) => {
                  const feet = Math.floor((i + 50) / 12);
                  const inches = (i + 50) % 12;
                  return {
                    value: `${i + 50}`,
                    label: `${feet}'${inches}"`
                  };
                }).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Ethnicity</InputLabel>
              <Select
                multiple
                name="ethnicities"
                value={formData.ethnicities || []}
                onChange={handleMultipleSelect}
                label="Ethnicities"
                renderValue={(selected) => {
                  const ethnicityMap: { [key: string]: string } = {
                    "black": "Black",
                    "eastAsian": "East Asian",
                    "white": "White",
                    "middleEastern": "Middle Eastern",
                    "southAsian": "South Asian",
                    "latinAmerican": "Hispanic/Latino",
                    "indigenous": "Indigenous",
                    "other": "Other"
                  };
                  return (selected as string[]).map(value => ethnicityMap[value]).join(', ');
                }}
              >
                {[
                  { value: "black", label: "Black" },
                  { value: "eastAsian", label: "East Asian" },
                  { value: "white", label: "White" },
                  { value: "middleEastern", label: "Middle Eastern" },
                  { value: "southAsian", label: "South Asian" },
                  { value: "latinAmerican", label: "Hispanic/Latino" },
                  { value: "indigenous", label: "Indigenous" },
                  { value: "other", label: "Other" }
                ].map((ethnicity) => (
                  <MenuItem key={ethnicity.value} value={ethnicity.value}>
                    <Checkbox checked={(formData.ethnicities || []).indexOf(ethnicity.value) > -1} />
                    <ListItemText primary={ethnicity.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Current Citizenship</InputLabel>
              <Select
                multiple
                name="nationality"
                value={formData.nationality || []}
                onChange={handleMultipleSelect}
                label="Nationality"
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    <Checkbox checked={(formData.nationality || []).indexOf(country) > -1} />
                    <ListItemText primary={country} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Interests"
              name="interests"
              value={formData.interests}
              onChange={handleChange}
              helperText="Separate interests with commas"
              required
            />

            <TextField
              fullWidth
              label="Occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Education"
              name="education"
              value={formData.education}
              onChange={handleChange}
              required
            />

            <Button 
              type="submit" 
              variant="contained" 
              fullWidth
              sx={{ mt: 1, color: 'white' }}
            >
              Register
            </Button>
          </Box>
        </LocalizationProvider>
        
        <Button 
          onClick={() => navigate('/login')}
          variant="text"
          fullWidth
        >
          Already have an account? Login
        </Button>
      </Box>
    </Container>
  );
};

export default RegisterForm;